import { DateTime } from 'luxon'
import { TFunction } from 'i18next'
import { FolderDriveItem } from '../components/Document/DocumentFlatTree'

// Caps to keep zip jobs within the Azure Functions timeout and typical Exchange message-size
// limits. Folders carry their aggregated size, so the size check covers nested content too.
export const ZIP_MAX_FILE_COUNT = 100

export type ZipParams = {
    itemIds: string[]
    folderIds: string[]
    fileName: string
    rootFolderId: string
}

export function buildZipFileName(reference: string): string {
    return `${reference} - documenten - ${DateTime.now().toFormat('yyyy-MM-dd HHmm')}.zip`
}

export function buildZipParams(selectedDriveItems: FolderDriveItem[], documentRootId: string, reference: string): ZipParams {
    return {
        itemIds: selectedDriveItems.filter(item => item.itemType === 'leaf').map(item => item.id),
        folderIds: selectedDriveItems.filter(item => item.itemType === 'branch').map(item => item.id),
        fileName: buildZipFileName(reference),
        rootFolderId: documentRootId,
    }
}

// Attachments end up base64-encoded inside the message, so only ~70% of the mailbox's
// message-size limit is usable as attachment budget.
const MESSAGE_SIZE_ATTACHMENT_RATIO = 0.7

// Fail fast before dispatching the zip job: the tree already carries the DriveItem metadata, so an
// oversized selection never reaches the backend. Folder child counts only cover direct children,
// which makes the count check a best effort — the size check is the authoritative one.
// The effective size cap: the static maximum, tightened to the mailbox's own message-size limit
// (from Graph getMailTips, see useMaxMessageSize) when that is known and smaller.
export function validateZipSelection(selectedDriveItems: FolderDriveItem[], translate: TFunction, maxMessageSize?: number): Error | undefined {
    const fileCount = selectedDriveItems.reduce((count, item) => count + (item.itemType === 'leaf' ? 1 : (item.childCount ?? 0)), 0)
    const totalSize = selectedDriveItems.reduce((size, item) => size + (item.size ?? 0), 0)
    const maxTotalSize = Math.floor(maxMessageSize * MESSAGE_SIZE_ATTACHMENT_RATIO)
    if (fileCount > ZIP_MAX_FILE_COUNT || totalSize > maxTotalSize) {
        return new Error(
            translate('file.pivot.document.email.add.zip.error', {
                count: ZIP_MAX_FILE_COUNT,
                size: Math.floor(maxTotalSize / (1024 * 1024)),
            }),
        )
    }
    return undefined
}
